import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  AppBar, 
  Toolbar, 
  Typography, 
  Select, 
  MenuItem, 
  Box, 
  Button, 
  FormControl, 
  InputLabel,
  Divider,
  Avatar,
  IconButton,
  Menu,
  ListItemIcon,
  Container,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Paper
} from '@mui/material';
import './Navigation.css';

// Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SecurityIcon from '@mui/icons-material/Security';
import LogoutIcon from '@mui/icons-material/Logout';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import TimelineIcon from '@mui/icons-material/Timeline';
import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import GroupIcon from '@mui/icons-material/Group';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useDesignerUI } from './signing/DesignerUIContext';

const drawerWidth = 240;
const Logo = '/seabird_hero_logo.png';

const Navigation = () => {
  const [buildings, setBuildings] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [filteredIndividuals, setFilteredIndividuals] = useState([]);
  const { selectedBuilding, setSelectedBuilding, username, userRole, user } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Extract first name from user data or email
  const getFirstName = () => {
    // Try to get name from user object first
    if (user?.name) {
      return user.name.split(' ')[0];
    }
    // Fall back to email prefix, capitalize it
    if (username && username.includes('@')) {
      const prefix = username.split('@')[0];
      // Handle formats like "jsmith" or "john.smith"
      const namePart = prefix.split('.')[0];
      return namePart.charAt(0).toUpperCase() + namePart.slice(1).toLowerCase();
    }
    return username || 'User';
  };
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { leftPanelContent } = useDesignerUI();
  
  // Track active navigation item
  const isActive = (path) => {
    if (location.pathname === path) {
      return true;
    }
    return location.pathname.startsWith(`${path}/`);
  };

  // Menu handlers
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose(); // Close the menu first
    navigate('/logout'); // Then navigate to logout
  };

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const response = await fetchWithAuth('/api/locations');
        
        if (response.ok) {
          const data = await response.json();
          setBuildings(data);
          
          // If no building is selected, set the first one as default
          // or set "All" option if it exists
          if (!selectedBuilding) {
            const allOption = data.find(b => b.name === "All");
            if (allOption) {
              setSelectedBuilding(allOption);
            } else if (data.length > 0) {
              setSelectedBuilding(data[0]);
            }
          }
        } else {
          console.error('Failed to fetch buildings:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching buildings:', error);
      }
    };

    const fetchIndividuals = async () => {
      try {
        const response = await fetchWithAuth('/api/individuals');
        const data = await response.json();
        setIndividuals(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching individuals:', error);
      }
    };

    fetchBuildings();
    fetchIndividuals();
  }, []);

  // Filter individuals whenever selectedBuilding changes
  useEffect(() => {
    if (selectedBuilding && Array.isArray(individuals)) {
      if (selectedBuilding.name === "All") {
        setFilteredIndividuals(individuals);
      } else {
        setFilteredIndividuals(
          individuals.filter(
            (individual) => individual.building_location_id === selectedBuilding.id
          )
        );
      }
    } else {
      setFilteredIndividuals([]);
    }
  }, [selectedBuilding, individuals]);

  const handleBuildingChange = (event) => {
    const buildingId = event.target.value;
    const building = buildings.find((b) => b.id === parseInt(buildingId, 10));
    
    if (building) {
      console.log("Selected building:", building);
      setSelectedBuilding(building);
      
      // Navigate to dashboard when building changes to refresh data
      if (location.pathname === '/dashboard') {
        // Trigger a refresh of the dashboard data
        navigate('/dashboard', { replace: true });
      }
    }
  };

  const handleIndividualSelect = (event) => {
    const individualId = event.target.value;
    if (individualId) {
      navigate(`/people/${individualId}`);
    }
  };

  // Check if user has admin permissions
  const isAdmin = useMemo(() => {
    return userRole?.can_manage_users || userRole?.can_view_audit_logs;
  }, [userRole]);

  const canViewAuditLogs = useMemo(() => {
    return userRole?.can_view_audit_logs;
  }, [userRole]);

  const menuItems = useMemo(() => {
    const items = [
      { text: 'Dashboard', icon: DashboardIcon, path: '/dashboard' },
      { text: 'Individuals', icon: PeopleIcon, path: '/individuals' },
      { text: 'Add Individual', icon: PersonAddIcon, path: '/add-user' },
      { text: 'Timeline', icon: TimelineIcon, path: '/timeline' },
      // { text: 'Signing', icon: DescriptionIcon, path: '/signing' }, // Hidden until ready for production
      { text: 'Requests', icon: AssignmentIcon, path: '/requests' }
    ];

    // Add admin-only items
    if (isAdmin) {
      items.push({ text: 'Employees', icon: GroupIcon, path: '/employees' });
    }

    if (canViewAuditLogs) {
      items.push({ text: 'Audit Log', icon: HistoryIcon, path: '/audit-logs' });
    }

    return items;
  }, [isAdmin, canViewAuditLogs]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo and Title */}
      <Box sx={{ pt: 3, pb: 2, px: 2, textAlign: 'center' }}>
        <img
          src={Logo}
          alt="Seabird Forms Logo"
          style={{
            width: '120px',
            height: 'auto',
            marginBottom: '8px'
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          <span style={{ color: '#F4B52A' }}>Seabird</span> Forms
        </Typography>
      </Box>
      
      <Divider />

      <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Navigation Menu */}
        <List sx={{ pt: 2 }}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={isActive(item.path)}
                  onClick={() => setMobileDrawerOpen(false)}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: isActive(item.path) ? 'white' : 'primary.main',
                    minWidth: 40
                  }}>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {leftPanelContent && (
          <>
            <Divider sx={{ mt: 1 }} />
            <Box sx={{ px: 2, py: 2 }}>
              {leftPanelContent}
            </Box>
          </>
        )}
      </Box>

      <Divider />

      {/* Controls Section */}
      <Box sx={{ p: 2 }}>
        {/* Building Selection */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={selectedBuilding?.id || ''}
            onChange={handleBuildingChange}
            label="Location"
          >
            <MenuItem value="" disabled>
              <span>Select a location</span>
            </MenuItem>
            {buildings.map((building) => (
              <MenuItem key={building.id} value={building.id}>
                {building.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Individual Selection for People page */}
        {location.pathname.includes('/people') && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Individual</InputLabel>
            <Select
              value={location.pathname.split('/').pop() || ''}
              onChange={handleIndividualSelect}
              label="Individual"
            >
              <MenuItem value="" disabled>
                <span>Select an individual</span>
              </MenuItem>
              {filteredIndividuals.map((individual) => (
                <MenuItem key={individual.id} value={individual.id}>
                  {individual.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {/* User greeting */}
        {username && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.50', 
              mb: 2,
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Hello, {getFirstName()}
            </Typography>
          </Paper>
        )}
        
        {/* Logout Button */}
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ mt: 1 }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top AppBar for mobile */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          display: { sm: 'none' },
          background: 'linear-gradient(130deg, #0C5085 0%, #162248 100%)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => setMobileDrawerOpen(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            <span style={{ color: '#F4B52A' }}>Seabird</span> Forms
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth
            }
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 1,
              borderColor: 'divider'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* User menu for mobile - positioned absolutely */}
      {isMobile && (
        <Box sx={{ position: 'fixed', top: 8, right: 8, zIndex: 1300 }}>
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{ 
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
              }}
            >
              <AccountCircleIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};

export default Navigation;
